import { useNavigate } from 'react-router-dom';
import './System.css';
import { useSystem } from '../../context/SystemContext';
import { COMPONENT_STATUS, CONNECTION_STATUS, SENSOR_TYPES, CSS_STATUS_CLASS } from '../../context/enums';

/**
 * System component that displays sensors, relays, and connection status with interactive controls
 * @returns {JSX.Element} The system management interface
 */
const System = () => {
  const navigate = useNavigate();
  const {
    relays,
    sensors,
    externalSensors,
    connectionStatus,
    getDisplayStatus,
    getStatusColorClass,
    getSensorName,
    getRelayName
  } = useSystem();
  
  /**
   * Determines the CSS status class for a sensor based on connection and operational status
   * @param {Object} sensor - The sensor object to evaluate
   * @param {string} sensor.connection - Connection status of the sensor
   * @param {string} sensor.status - Operational status of the sensor
   * @param {string} sensor.type - Type of sensor (water, signal, etc.)
   * @returns {string} CSS class name for styling the sensor tile
   */
  const getSensorStatusClass = (sensor) => {
    if (sensor.connection) {
      const connection = sensor.connection.toLowerCase();
      if (connection === CONNECTION_STATUS.DISCONNECTED) {
        return CSS_STATUS_CLASS.DISCONNECTED;
      }
      if (connection === CONNECTION_STATUS.CONNECTED) {
        const status = sensor.status?.toLowerCase();
        if (status === COMPONENT_STATUS.ACTIVE) return CSS_STATUS_CLASS.ALERT;
        if (status === COMPONENT_STATUS.INACTIVE) return CSS_STATUS_CLASS.ERROR;
        return CSS_STATUS_CLASS.READY;
      }
    }

    if (sensor.status === COMPONENT_STATUS.ACTIVE) return CSS_STATUS_CLASS.ALERT;

    if (sensor.type === SENSOR_TYPES.WATER) {
      const hoursMatch = sensor.status.match(/([\d.]+)/);
      if (hoursMatch) {
        const hours = parseFloat(hoursMatch[1]);
        return hours >= 2 ? CSS_STATUS_CLASS.READY : CSS_STATUS_CLASS.DISCONNECTED;
      }
    }

    if (sensor.type === SENSOR_TYPES.SIGNAL) {
      return ['Strong', 'Moderate'].includes(sensor.status)
        ? CSS_STATUS_CLASS.READY
        : CSS_STATUS_CLASS.DISCONNECTED;
    }

    return CSS_STATUS_CLASS.DISCONNECTED;
  };
  
  /**
   * Determines the CSS status class for a relay based on connection and operational status
   * @param {Object} relay - The relay object to evaluate
   * @param {string} relay.connection - Connection status of the relay
   * @param {string} relay.status - Operational status of the relay
   * @returns {string} CSS class name for styling the relay tile
   */
  const getRelayStatusClass = (relay) => {
    if (relay.connection) {
      const connection = relay.connection.toLowerCase();
      if (connection === CONNECTION_STATUS.DISCONNECTED) {
        return CSS_STATUS_CLASS.DISCONNECTED;
      }
      if (connection === CONNECTION_STATUS.CONNECTED) {
        const status = relay.status?.toLowerCase();
        if (status === COMPONENT_STATUS.ACTIVE) return CSS_STATUS_CLASS.ALERT;
        if (status === COMPONENT_STATUS.INACTIVE) return CSS_STATUS_CLASS.ERROR;
        return CSS_STATUS_CLASS.READY;
      }
    }

    if (relay.status) {
      const status = relay.status.toLowerCase();
      if (status === COMPONENT_STATUS.ACTIVE) return CSS_STATUS_CLASS.ALERT;
      if (status === COMPONENT_STATUS.STANDBY) return CSS_STATUS_CLASS.READY;
      if (status === COMPONENT_STATUS.INACTIVE) return CSS_STATUS_CLASS.ERROR;
    }

    return CSS_STATUS_CLASS.DISCONNECTED;
  };

  const allSensors = [...(sensors || []), ...(externalSensors || [])];
  
  /**
   * Handles sensor tile click events and navigates to sensor detail page
   * @param {string} sensorName - The name of the sensor that was clicked
   */
  const handleSensorClick = (sensorName) => {
    navigate(`/system/sensor/${sensorName}`);
  };

  /**
   * Handles relay tile click events and navigates to relay detail page
   * @param {string} relayId - The ID of the relay that was clicked
   */
  const handleRelayClick = (relayId) => {
    navigate(`/system/relay/${relayId}`);
  };

  /**
   * Gets the CSS class for the connection status indicator
   * @returns {string} CSS class name for connection status styling
   */
  const getConnectionStatusClass = () => {
    switch(connectionStatus) {
      case CONNECTION_STATUS.CONNECTED: return 'connection-connected';
      case CONNECTION_STATUS.ERROR: return 'connection-error';
      default: return 'connection-disconnected';
    }
  };

  /**
   * Gets the human-readable text for the current connection status
   * @returns {string} Descriptive text for the connection status
   */
  const getConnectionStatusText = () => {
    switch(connectionStatus) {
      case CONNECTION_STATUS.CONNECTED: return 'System Connected';
      case CONNECTION_STATUS.ERROR: return `Connection Error`;
      default: return 'System Disconnected';
    }
  };
  
  return (
    <div className="system-page">
      <div className="connection-status">
        <div className={`connection-indicator ${getConnectionStatusClass()}`}>
          <div className="connection-dot"></div>
          <span className="connection-text">{getConnectionStatusText()}</span>
        </div>
      </div>
      
      <div className="sensor-section">
        <h3>Sensors</h3>
        <div className="sensor-grid">
          {allSensors.length === 0 ? (
            <div className="no-devices-message">
              <div className="no-devices-text">No sensors detected</div>
              <div className="no-devices-subtext">Connect to a device to see available sensors</div>
            </div>
          ) : (
            allSensors.map(sensor => (
              <div
                key={sensor.id}
                className={`sensor-tile ${sensor.type} ${getSensorStatusClass(sensor)}`}
                onClick={() => handleSensorClick(sensor.name)}
                style={{ cursor: 'pointer' }}
              >
                <div className="sensor-icon">{sensor.icon}</div>
                <div className="sensor-name">{getSensorName(sensor.id)}</div>
                <div className={`sensor-status ${getStatusColorClass(sensor.status)}`}>
                  {getDisplayStatus(sensor.status)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      <div className="relay-section">
        <h3>Relays</h3>
        <div className="relay-grid">
          {relays.length === 0 ? (
            <div className="no-devices-message">
              <div className="no-devices-text">No relays detected</div>
              <div className="no-devices-subtext">Connect to a device to see available relays</div>
            </div>
          ) : (
            relays.map(relay => (
              <div
                key={relay.id}
                className={`relay-tile ${getRelayStatusClass(relay)}`}
                onClick={() => handleRelayClick(relay.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="relay-icon">{relay.icon}</div>
                <div className="relay-name">{getRelayName(relay.id)}</div>
                <div className={`relay-status ${relay.status.toLowerCase()} ${getStatusColorClass(relay.status)}`}>
                  {getDisplayStatus(relay.status)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
    </div>
  );
};

export default System;